import {
  Offer,
  OfferCategory,
  SectionOffer,
  SingleSectionSettings,
  resolveSectionNumberOfOffers,
} from "../../../../types/global.types";

export type SlottedOffer = { slot: number; offer: Offer };

export type DisplayedSectionOffers = {
  featuredOffers: Offer[];
  displaySlotted: SlottedOffer[];
};

const getSectionOfferId = (item: SectionOffer | string | undefined) => {
  if (!item) return "";
  if (typeof item === "string") return String(item);
  return String(item.id || "");
};

export const resolveSectionOffers = (
  selected: SectionOffer[] | undefined,
  campaignOffers: Offer[]
): Offer[] =>
  (selected || [])
    .map((item) => {
      const selectedId = getSectionOfferId(item);
      if (!selectedId) return undefined;
      return campaignOffers.find((offer) => String(offer.id) === selectedId);
    })
    .filter((offer): offer is Offer => Boolean(offer));

export const resolveSlottedOffers = (
  selected: SectionOffer[] | undefined,
  campaignOffers: Offer[]
): SlottedOffer[] => {
  const usedSlots = new Set<number>();
  const placed: SlottedOffer[] = [];
  const unslotted: Offer[] = [];

  (selected || []).forEach((item) => {
    const selectedId = getSectionOfferId(item);
    if (!selectedId) return;

    const offer = campaignOffers.find(
      (campaignOffer) => String(campaignOffer.id) === selectedId
    );
    if (!offer) return;

    const savedSlot =
      typeof item === "object" && typeof item.slot === "number"
        ? item.slot
        : undefined;

    if (
      typeof savedSlot === "number" &&
      savedSlot >= 0 &&
      !usedSlots.has(savedSlot)
    ) {
      usedSlots.add(savedSlot);
      placed.push({ slot: savedSlot, offer });
      return;
    }

    unslotted.push(offer);
  });

  let nextSlot = 0;
  unslotted.forEach((offer) => {
    while (usedSlots.has(nextSlot)) nextSlot += 1;
    usedSlots.add(nextSlot);
    placed.push({ slot: nextSlot, offer });
    nextSlot += 1;
  });

  return placed
    .sort((a, b) => a.slot - b.slot)
    .map((item, index) => ({ ...item, slot: index }));
};

const getCategoryId = (category?: OfferCategory | null) =>
  String(category?.category_id ?? "").trim();

const getCategoryNameKey = (category?: OfferCategory | null) =>
  (category?.name ?? "").trim().toLowerCase();

const expandSectionCategoryIds = (
  campaignOffers: Offer[],
  categoryIds?: string[]
): string[] | undefined => {
  if (!categoryIds?.length) return categoryIds;

  const selectedIds = new Set(categoryIds.map((id) => String(id).trim()));
  const selectedNames = new Set<string>();

  campaignOffers.forEach((offer) => {
    (offer.categories || []).forEach((category) => {
      if (!selectedIds.has(getCategoryId(category))) return;
      const nameKey = getCategoryNameKey(category);
      if (nameKey) selectedNames.add(nameKey);
    });
  });

  campaignOffers.forEach((offer) => {
    (offer.categories || []).forEach((category) => {
      const nameKey = getCategoryNameKey(category);
      if (!nameKey || !selectedNames.has(nameKey)) return;
      const id = getCategoryId(category);
      if (id) selectedIds.add(id);
    });
  });

  return Array.from(selectedIds);
};

const offerMatchesSectionCategory = (
  offer: Offer,
  categoryIds?: string[]
): boolean => {
  if (!categoryIds?.length) return true;
  const selected = new Set(categoryIds.map((id) => String(id).trim()));
  return (offer.categories || []).some((category) =>
    selected.has(getCategoryId(category))
  );
};

const fillFromCategoryLeftovers = (
  slotted: SlottedOffer[],
  leftovers: Offer[],
  categoryIds: string[] | undefined,
  fill: (
    matching: Offer[]
  ) => { slotted: SlottedOffer[]; leftovers: Offer[] }
): { slotted: SlottedOffer[]; leftovers: Offer[] } => {
  const matching = leftovers.filter((offer) =>
    offerMatchesSectionCategory(offer, categoryIds)
  );
  const distributed = fill(matching);
  const addedIds = new Set(
    distributed.slotted.slice(slotted.length).map((item) => item.offer.id)
  );

  return {
    slotted: distributed.slotted,
    leftovers: leftovers.filter((offer) => !addedIds.has(offer.id)),
  };
};

export const getUnusedCampaignOffers = (
  campaignOffers: Offer[],
  sections: SingleSectionSettings[]
) => {
  const usedIds = new Set(
    sections
      .flatMap((section) => [
        ...(section.featuredOffers || []).map((item) => getSectionOfferId(item)),
        ...(section.offers || []).map((item) => getSectionOfferId(item)),
      ])
      .filter(Boolean)
  );

  return campaignOffers.filter((offer) => !usedIds.has(String(offer.id)));
};

const fillLastRowWithLeftoverOffers = (
  slotted: SlottedOffer[],
  leftovers: Offer[],
  columns: number
): { slotted: SlottedOffer[]; leftovers: Offer[] } => {
  if (!leftovers.length || columns <= 0) {
    return { slotted, leftovers };
  }

  const remainder = slotted.length % columns;
  const emptySlots =
    slotted.length === 0 ? columns : remainder === 0 ? 0 : columns - remainder;

  if (emptySlots === 0) {
    return { slotted, leftovers };
  }
  const toFill = leftovers.slice(0, emptySlots);
  const remaining = leftovers.slice(toFill.length);
  const startSlot = slotted.length;

  return {
    slotted: [
      ...slotted,
      ...toFill.map((offer, index) => ({
        slot: startSlot + index,
        offer,
      })),
    ],
    leftovers: remaining,
  };
};

const fillToNumberOfOffers = (
  slotted: SlottedOffer[],
  leftovers: Offer[],
  count: number,
  sliceIfOver = true
): { slotted: SlottedOffer[]; leftovers: Offer[] } => {
  const target = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  if (target <= 0) {
    return { slotted, leftovers };
  }

  if (slotted.length >= target) {
    return {
      slotted: sliceIfOver ? slotted.slice(0, target) : slotted,
      leftovers,
    };
  }

  const needed = target - slotted.length;
  const toFill = leftovers.slice(0, needed);
  const remaining = leftovers.slice(toFill.length);
  const startSlot = slotted.length;

  return {
    slotted: [
      ...slotted,
      ...toFill.map((offer, index) => ({
        slot: startSlot + index,
        offer,
      })),
    ],
    leftovers: remaining,
  };
};

export const getDisplayedSectionOffers = (
  sections: SingleSectionSettings[],
  campaignOffers: Offer[]
): DisplayedSectionOffers[] => {
  let leftoverOffers = getUnusedCampaignOffers(campaignOffers, sections);

  return sections.map((section) => {
    const featuredOffers = resolveSectionOffers(
      section.featuredOffers,
      campaignOffers
    );
    const slottedOffers = resolveSlottedOffers(section.offers, campaignOffers);
    let displaySlotted = slottedOffers;
    const columnCount = Number(section.columns);
    const sectionCategoryIds = expandSectionCategoryIds(
      campaignOffers,
      section.offerCategoryId
    );
    const filled = fillFromCategoryLeftovers(
      slottedOffers,
      leftoverOffers,
      sectionCategoryIds,
      (matching) =>
        fillToNumberOfOffers(
          slottedOffers,
          matching,
          resolveSectionNumberOfOffers(section),
          section.columns === "auto"
        )
    );
    displaySlotted = filled.slotted;
    leftoverOffers = filled.leftovers;

    if (
      section.columns !== "auto" &&
      section.distributeOffers &&
      Number.isFinite(columnCount) &&
      columnCount > 0
    ) {
      const distributed = fillFromCategoryLeftovers(
        displaySlotted,
        leftoverOffers,
        sectionCategoryIds,
        (matching) =>
          fillLastRowWithLeftoverOffers(displaySlotted, matching, columnCount)
      );
      displaySlotted = distributed.slotted;
      leftoverOffers = distributed.leftovers;
    }

    return { featuredOffers, displaySlotted };
  });
};

export const getDisplayedSectionOfferCount = (
  displayed: DisplayedSectionOffers | undefined
) =>
  (displayed?.featuredOffers.length || 0) +
  (displayed?.displaySlotted.length || 0);
